import * as Redux from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import type { IAppState } from '~/appstate';
import type { RemoteData } from '../async-actions';
import type { IDropdownItem } from '../components/Dropdown';
import { getSession } from '../helpers/Session';
import { toastr } from '../helpers/Toastr';
import * as Hub from '../ogm-hub';
import { getSiteTypeFromId } from './shared';

export interface NewRestrictionListState {
	availableSuppliers: RemoteData<IDropdownItem[], string[]>;
	newListSupplier: IDropdownItem | null;
	newListName: string;
	newListSubunitIds: number[];
	supplierSectionVisible: boolean;
	canSave: boolean;
	canCancel: boolean;
	siteType: 'dssi' | 'sysco' | null;
}

function makeInitialState(): NewRestrictionListState {
	return {
		availableSuppliers: { type: 'initial' },
		newListSupplier: null,
		newListName: '',
		newListSubunitIds: [],
		supplierSectionVisible: false,
		canSave: false,
		canCancel: true,
		siteType: null,
	};
}

export const slice = Redux.createSlice({
	name: 'newRestrictionList',
	initialState: makeInitialState(),
	reducers: {
		newRestrictionListPagePending(state, action) {
			const siteType = getSiteTypeFromId(action.payload.siteTypeId);
			const supplierSectionVisible = siteType === 'dssi';
			state.availableSuppliers = { type: 'loading' };
			state.supplierSectionVisible = supplierSectionVisible;
			state.siteType = siteType;
		},
		newRestrictionListPageFailure(state, action) {
			state.availableSuppliers = {
				type: 'failure',
				err: [
					`The server could not respond to the request: HTTP Status ${action.payload.response.status}`,
				],
			};
		},
		newRestrictionListPageSuccess(state, action) {
			const availableSuppliersData = action.payload.suppliers.map((x) => ({
				value: x.id.toString(),
				display: x.name,
			}));
			state.availableSuppliers = {
				type: 'success',
				data: availableSuppliersData,
			};

			if (state.siteType === 'sysco') {
				const suppliers = state.availableSuppliers;
				const syscoSupplier = action.payload.suppliers.find(
					(x) => x.dssiCode === 'SYSD'
				);
				const newListSupplier =
					suppliers.type === 'success'
						? suppliers.data.find((x) => x.display === syscoSupplier.name)
						: null;

				state.newListSupplier = newListSupplier;
			}
		},
		formChanged(state, action) {
			const value = action.payload.value;

			if (action.payload.field === 'name') {
				state.newListName = value;
			} else {
				const suppliers = state.availableSuppliers;
				const newListSupplier =
					suppliers.type === 'success'
						? suppliers.data.find((x) => x.value === value)
						: null;

				state.newListSupplier = newListSupplier;
			}

			const nameValid = !!state.newListName.trim();
			const supplierValid =
				state.siteType === 'sysco' ? true : !!state.newListSupplier;
			const canSave = nameValid && supplierValid;

			state.canSave = canSave;
		},
		newRestrictionListCreatedPending(state) {
			state.canSave = false;
			state.canCancel = false;
		},
		newRestrictionListCreatedFailure(state) {
			state.canSave = true;
			state.canCancel = true;
		},
		resetState: () => makeInitialState(),
	},
});

export type NewRestrictionListFormChangedPayload =
	| { field: 'supplier'; value: string }
	| { field: 'name'; value: string };

export const thunks = {
	newRestrictionListPageLoaded() {
		return async (dispatch) => {
			const siteTypeId = getSession().siteTypeId;
			dispatch(
				slice.actions.newRestrictionListPagePending({
					siteTypeId,
				})
			);

			const response = await Hub.RestrictionsController.GetNewListPageData();
			if (!response.ok) {
				dispatch(slice.actions.newRestrictionListPageFailure(response));
				return;
			}

			dispatch(slice.actions.newRestrictionListPageSuccess(response.value));
		};
	},
	newRestrictionListCreated() {
		return async (dispatch, getState: () => IAppState) => {
			dispatch(slice.actions.newRestrictionListCreatedPending());

			const state = getState();
			const supplier = state.newRestrictionList.newListSupplier;
			const supplierId = supplier ? parseInt(supplier.value, 10) : null;
			const name = state.newRestrictionList.newListName;
			const subunitIds = state.newRestrictionList.newListSubunitIds;
			const apiArgs = {
				supplierId,
				name,
				subunitIds,
			};
			const response = await Hub.RestrictionsController.CreateRestrictionList(
				apiArgs
			).then((res) => {
				if (res.ok) {
					dispatch(push('/restrictions'));
				}

				if (res.kind === 'failure') {
					const message =
						state.newRestrictionList.siteType === 'dssi'
							? 'There was an issue while attempting to create your restriction list. Make sure there are no other restriction lists with this name.'
							: 'There was an issue while attempting to create your restriction list.';
					toastr.error({
						title: 'Error',
						body: message,
					});
				}

				return res;
			});

			if (!response.ok) {
				dispatch(slice.actions.newRestrictionListCreatedFailure());
			}
		};
	},
	newRestrictionListFormChanged(payload: NewRestrictionListFormChangedPayload) {
		return (dispatch) => {
			dispatch(slice.actions.formChanged(payload));
		};
	},
	newRestrictionListCanceled() {
		return (dispatch) => {
			dispatch(push('/restrictions'));
		};
	},
};
